const API_URL = "https://stock.amservices.eu/api"; // L'URL de ton backend

export const stockService = {
    getScannerStatus: async () => {
        const response = await fetch(`${API_URL}/scanner_delivery.php?action=status`, { cache: 'no-store' });
        const data = await response.json();
        if (!response.ok || !data.success) throw new Error(data.message || "Scanner FTP indisponible");
        return data;
    },
    retryLatestScannedDelivery: async () => {
        const response = await fetch(`${API_URL}/scanner_delivery.php`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ action: 'retry_latest' })
        });
        const data = await response.json();
        if (!response.ok || !data.success) throw new Error(data.message || "Impossible de relancer le BL");
        return data;
    },
    getNextScannedDelivery: async () => {
        const response = await fetch(`${API_URL}/scanner_delivery.php?action=next`, { cache: 'no-store' });
        const data = await response.json();
        // Compatibilite avec l'ancienne API pendant sa republication : un
        // conflit signifie seulement qu'un autre onglet traite deja le BL.
        if (response.status === 409) return { success: true, file: null, already_claimed: true };
        if (!response.ok || !data.success) throw new Error(data.message || "Impossible de lire la file FTP du scanner");
        return data;
    },
    finishScannedDelivery: async (token, success) => {
        const response = await fetch(`${API_URL}/scanner_delivery.php`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ action: success ? 'complete' : 'reject', token })
        });
        const data = await response.json();
        if (!response.ok || !data.success) throw new Error(data.message || "Impossible d'archiver le scan");
        return data;
    },
    analyzeDeliveryWithAi: async (image, ocrText = '') => {
        let imageDataUrl = '';
        if (!String(ocrText).trim()) {
            imageDataUrl = await new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = () => resolve(reader.result);
                reader.onerror = () => reject(new Error("Impossible de préparer la photo"));
                reader.readAsDataURL(image);
            });
            const approximateBytes = Math.floor((String(imageDataUrl).split(',')[1]?.length || 0) * 0.75);
            if (approximateBytes > 350 * 1024) {
                throw new Error("La photo reste trop lourde après compression. Reprends-la en cadrant uniquement le document.");
            }
        }
        const controller = new AbortController();
        const timeout = window.setTimeout(() => controller.abort(), 30000);
        let response;
        try {
            response = await fetch(`${API_URL}/analyze_delivery_ai.php`, {
            method: 'POST',
            signal: controller.signal,
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ image: imageDataUrl, ocr_text: String(ocrText).slice(0, 24000) }),
            });
        } catch (error) {
            if (error.name === 'AbortError') throw new Error("L’analyse IA a dépassé 30 secondes. Le serveur Groq ne répond pas assez vite.");
            throw error;
        } finally {
            window.clearTimeout(timeout);
        }
        const data = await response.json();
        if (!response.ok || !data.success) {
            throw new Error(data.message || "Impossible d'analyser la photo avec l'IA");
        }
        return data.analysis;
    },
    // Mise à jour rapide uniquement du stock (utilisé par le scan/boutons +/-)
    updateProductStock: async (ref, newQty) => {
        const response = await fetch(`${API_URL}/update_stock.php`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ ref, stock: newQty })
        });
        return await response.json();
    },

    // NOUVEAU : Mise à jour complète d'un produit (nom, prix, catégorie, etc.)
    updateProduct: async (productData) => {
        const response = await fetch(`${API_URL}/update_product_full.php`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(productData)
        });
        return await response.json();
    },
    // Dans ton objet stockService
    deleteProduct: async (id) => {
        const response = await fetch(`${API_URL}/delete_product.php`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: id }) // On envoie l'ID ici
        });
        return await response.json();
    },
    // Récupérer tous les produits
    getAllProducts: async () => {
        const response = await fetch(`${API_URL}/produits.php`);
        return await response.json();
    },

    // Importation du fichier CSV
    importCSV: async (file) => {
        const formData = new FormData();
        formData.append('file', file);

        const response = await fetch(`${API_URL}/import_csv.php`, {
            method: 'POST',
            body: formData,
        });
        return await response.json();
    },

    // Ajouter au stock les quantites validees depuis un bon de livraison
    receiveDelivery: async (items) => {
        const response = await fetch(`${API_URL}/receive_delivery.php`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ items })
        });
        const data = await response.json();
        if (!response.ok) {
            throw new Error(data.message || "Impossible d'enregistrer la livraison");
        }
        return data;
    },

    // Récupérer un produit par son ID
    getProductById: async (id) => {
        const response = await fetch(`${API_URL}/produit_details.php?id=${id}`);
        return await response.json();
    },

    // Ajouter un nouveau produit manuellement
    addProduct: async (productData) => {
        const response = await fetch(`${API_URL}/add_product.php`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(productData)
        });
        return await response.json();
    },

    // Mettre à jour le stock via l'ID
    updateStock: async (id, nouvelleQuantite) => {
        const response = await fetch(`${API_URL}/update_stock.php`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id, stock: nouvelleQuantite })
        });
        return await response.json();
    },

    // Authentification
    login: async (username, password) => {
        const response = await fetch(`${API_URL}/auth/login.php`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ username, password })
        });
        return await response.json();
    }
};
